import { useState, useEffect } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import { Flame, Sparkles, Utensils, FileText } from 'lucide-react';
import SEO from '../components/SEO';

const getOptimizedImageUrl = (url, width = 600) => {
  if (!url) return '';
  if (url.startsWith('https://assets.auxodev.dk/') && !url.includes('/cdn-cgi/image/')) {
    return url.replace('https://assets.auxodev.dk/', `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=80,format=auto/`);
  }
  return url;
};

export default function Menu() {
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const location = useLocation();

  // Determine active category from path
  const activeCategory = location.pathname.endsWith('/forretter') 
    ? 'forretter' 
    : location.pathname.endsWith('/hovedretter') 
      ? 'hovedretter' 
      : location.pathname.endsWith('/tilbehoer') 
        ? 'tilbehoer' 
        : 'alle';

  useEffect(() => {
    const fetchMenu = async () => {
      try {
        setLoading(true);
        const response = await fetch('https://api.dev.auxo.dk/public/content/menu_items');
        if (!response.ok) {
          throw new Error('Kunne ikke hente menukortet.');
        }
        const data = await response.json();
        
        // Sort items by sort_order
        const sortedData = data.sort((a, b) => (a.sort_order || 999) - (b.sort_order || 999));
        setMenuItems(sortedData);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchMenu();
  }, []);

  // Filter items based on active category
  const filteredItems = activeCategory === 'alle'
    ? menuItems
    : menuItems.filter(item => item.category_name === activeCategory);

  // Group items for "Alle" view to show Forretter first, then Hovedretter, then Tilbehør
  const forretter = menuItems.filter(item => item.category_name === 'forretter');
  const hovedretter = menuItems.filter(item => item.category_name === 'hovedretter');
  const tilbehoer = menuItems.filter(item => item.category_name === 'tilbehoer');

  return (
    <>
      <SEO
        title={`${activeCategory === 'alle' ? 'Menukort' : activeCategory === 'forretter' ? 'Forretter' : activeCategory === 'hovedretter' ? 'Hovedretter' : 'Tilbehør'} | Thai Hot Chili`}
        description="Udforsk vores komplette digitale menukort med lækre forretter, hovedretter og tilbehør tilberedt af friske råvarer. Bestil take-away i Skibby i dag."
      />

      {/* Header Banner */}
      <section className="relative pt-56 pb-16 bg-brand-dark text-white text-left">
        <div className="absolute inset-0 z-0 opacity-20">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/6f940026-1e92-4aa1-ae41-4b36c3c69c15/thai-pad-thai-premium.jpg"
            alt="Thai Food Background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-brand-dark via-brand-dark/90 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">
            Vores Menukort
          </h1>
          <p className="text-gray-400 text-sm max-w-2xl mt-4 leading-relaxed">
            I Skibby står vi klar til at lave aftensmaden til dig. Om det er rød karry, kylling/cashew, sprøde forårsruller, stegte nudler eller noget helt andet så står vi klar til at lave din aftensmad.
          </p>

          {/* PDF Download Links */}
          <div className="mt-6 flex flex-wrap items-center justify-start gap-4">
            <a
              href="https://assets.auxodev.dk/assets/6f940026-1e92-4aa1-ae41-4b36c3c69c15/meny-feb-2026-orange.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-transparent border border-white text-white text-xs font-semibold uppercase tracking-wider px-5 py-2.5 rounded-full transition-all hover:opacity-80"
            >
              <FileText className="w-4 h-4 text-white" />
              Take-away menukort (PDF)
            </a>
            <a
              href="https://assets.auxodev.dk/assets/6f940026-1e92-4aa1-ae41-4b36c3c69c15/29-drikkevarer.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-transparent border border-white text-white text-xs font-semibold uppercase tracking-wider px-5 py-2.5 rounded-full transition-all hover:opacity-80"
            >
              <FileText className="w-4 h-4 text-white" />
              Drikkevarer (PDF)
            </a>
          </div>

          {/* Info Badge: Winter hours & Chili indicator */}
          <div className="mt-8 flex flex-wrap items-center gap-3 sm:gap-6 text-xs text-white/90 bg-white/10 backdrop-blur-xs border border-white/15 px-4 py-3 rounded-xl max-w-2xl">
            <div className="flex items-center gap-2">
              <span className="inline-block w-2 h-2 rounded-full bg-amber-400" />
              <span><strong className="text-white font-semibold">Vinteråbningstid:</strong> Mandag – fredag kl. 16.00 – 20.00</span>
            </div>
            <div className="hidden sm:block text-white/40">|</div>
            <div className="flex items-center gap-1.5">
              <span className="font-semibold text-white">Styrke:</span>
              <span>🌶 Mild</span>
              <span>·</span>
              <span>🌶🌶 Mellem</span>
              <span>·</span>
              <span>🌶🌶🌶 Stærk</span>
            </div>
          </div>
        </div>
      </section>

      {/* Menu Navigation Tabs */}
      <section className="bg-white border-b border-gray-100 sticky top-[72px] z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 flex flex-wrap justify-center gap-2 sm:gap-4 py-4">
          <Link
            to="/menukort"
            className={`px-6 py-2.5 rounded-full text-sm font-semibold tracking-wide transition-all ${
              activeCategory === 'alle'
                ? 'bg-brand-primary text-white shadow-md'
                : 'bg-brand-cream text-gray-600 hover:bg-gray-100'
            }`}
          >
            Hele Menukortet
          </Link>
          <Link
            to="/menukort/forretter"
            className={`px-6 py-2.5 rounded-full text-sm font-semibold tracking-wide transition-all ${
              activeCategory === 'forretter'
                ? 'bg-brand-primary text-white shadow-md'
                : 'bg-brand-cream text-gray-600 hover:bg-gray-100'
            }`}
          >
            Forretter
          </Link>
          <Link
            to="/menukort/hovedretter"
            className={`px-6 py-2.5 rounded-full text-sm font-semibold tracking-wide transition-all ${
              activeCategory === 'hovedretter'
                ? 'bg-brand-primary text-white shadow-md'
                : 'bg-brand-cream text-gray-600 hover:bg-gray-100'
            }`}
          >
            Hovedretter
          </Link>
          <Link
            to="/menukort/tilbehoer"
            className={`px-6 py-2.5 rounded-full text-sm font-semibold tracking-wide transition-all ${
              activeCategory === 'tilbehoer'
                ? 'bg-brand-primary text-white shadow-md'
                : 'bg-brand-cream text-gray-600 hover:bg-gray-100'
            }`}
          >
            Tilbehør
          </Link>
        </div>
      </section>

      {/* Menu Items Grid */}
      <section className="py-16 bg-brand-cream min-h-[500px]">
        <div className="max-w-7xl mx-auto px-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
              <div className="w-12 h-12 border-4 border-brand-primary border-t-transparent rounded-full animate-spin" />
              <p className="text-gray-500 font-medium">Henter menukortet...</p>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-100 text-red-700 p-6 rounded-2xl text-center max-w-lg mx-auto">
              <p className="font-semibold">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="mt-4 bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-semibold"
              >
                Prøv igen
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-16">
              {/* Forretter Section (Visible if 'alle' or 'forretter' is active) */}
              {(activeCategory === 'alle' || activeCategory === 'forretter') && (
                <div className="flex flex-col gap-8">
                  <div className="border-b border-gray-200 pb-4">
                    <h2 className="font-display text-2xl sm:text-3xl font-bold text-brand-dark">
                      Forretter
                    </h2>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {(activeCategory === 'alle' ? forretter : filteredItems).map((item) => (
                      <div
                        key={item.id}
                        className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 flex flex-col h-full hover:shadow-md transition-shadow"
                      >
                        {item.image && item.image[0] && (
                          <div className="h-48 overflow-hidden relative shrink-0">
                            <img
                              src={getOptimizedImageUrl(item.image[0], 600)}
                              alt={item.title}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}
                        <div className="p-6 flex flex-col flex-grow gap-3">
                          <div className="flex items-start justify-between gap-4">
                            <h3 className="font-display text-lg font-bold text-brand-dark leading-tight">
                              {item.title}
                            </h3>
                          </div>
                          {item.description && (
                            <p className="text-xs text-gray-600 leading-relaxed flex-grow">
                              {item.description}
                            </p>
                          )}
                          <div className="flex items-center justify-between pt-4 border-t border-gray-50 mt-auto">
                            <span className="text-xs font-semibold text-gray-400 uppercase">Pris</span>
                            <span className="font-display text-lg font-bold text-brand-primary">
                              kr. {item.price},-
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Hovedretter Section (Visible if 'alle' or 'hovedretter' is active) */}
              {(activeCategory === 'alle' || activeCategory === 'hovedretter') && (
                <div className="flex flex-col gap-8">
                  <div className="border-b border-gray-200 pb-4">
                    <h2 className="font-display text-2xl sm:text-3xl font-bold text-brand-dark">
                      Hovedretter
                    </h2>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {(activeCategory === 'alle' ? hovedretter : filteredItems).map((item) => (
                      <div
                        key={item.id}
                        className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 flex flex-col h-full hover:shadow-md transition-shadow"
                      >
                        {item.image && item.image[0] && (
                          <div className="h-48 overflow-hidden relative shrink-0">
                            <img
                              src={getOptimizedImageUrl(item.image[0], 600)}
                              alt={item.title}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}
                        <div className="p-6 flex flex-col flex-grow gap-3">
                          <div className="flex items-start justify-between gap-4">
                            <h3 className="font-display text-lg font-bold text-brand-dark leading-tight">
                              {item.title}
                            </h3>
                          </div>
                          {item.description && (
                            <p className="text-xs text-gray-600 leading-relaxed flex-grow">
                              {item.description}
                            </p>
                          )}
                          <div className="flex items-center justify-between pt-4 border-t border-gray-50 mt-auto">
                            <span className="text-xs font-semibold text-gray-400 uppercase">Pris</span>
                            <span className="font-display text-lg font-bold text-brand-primary">
                              kr. {item.price},-
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tilbehør Section (Visible if 'alle' or 'tilbehoer' is active) */}
              {(activeCategory === 'alle' || activeCategory === 'tilbehoer') && (
                <div className="flex flex-col gap-8">
                  <div className="border-b border-gray-200 pb-4 flex flex-col sm:flex-row sm:items-baseline justify-between gap-2">
                    <h2 className="font-display text-2xl sm:text-3xl font-bold text-brand-dark">
                      Tilbehør
                    </h2>
                    <p className="text-xs text-gray-500 italic">
                      * Ekstra tilvalg markeret med (!) kan kun købes sammen med en hovedret.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {(activeCategory === 'alle' ? tilbehoer : filteredItems).map((item) => (
                      <div
                        key={item.id}
                        className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 flex flex-col h-full hover:shadow-md transition-shadow"
                      >
                        {item.image && item.image[0] && (
                          <div className="h-48 overflow-hidden relative shrink-0">
                            <img
                              src={getOptimizedImageUrl(item.image[0], 600)}
                              alt={item.title}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}
                        <div className="p-6 flex flex-col flex-grow gap-3">
                          <div className="flex items-start justify-between gap-4">
                            <h3 className="font-display text-lg font-bold text-brand-dark leading-tight">
                              {item.title}
                            </h3>
                          </div>
                          {item.description && (
                            <p className="text-xs text-gray-600 leading-relaxed flex-grow">
                              {item.description}
                            </p>
                          )}
                          <div className="flex items-center justify-between pt-4 border-t border-gray-50 mt-auto">
                            <span className="text-xs font-semibold text-gray-400 uppercase">Pris</span>
                            <span className="font-display text-lg font-bold text-brand-primary">
                              kr. {item.price},-
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Order CTA Section */}
      <section className="py-20 bg-brand-dark text-white text-center">
        <div className="max-w-4xl mx-auto px-6 flex flex-col gap-6">
          <h2 className="font-display text-3xl font-bold">
            Klar til at bestille take-away?
          </h2>
          <p className="text-gray-200 max-w-xl mx-auto text-sm leading-relaxed">
            Ring direkte til vores take-away restaurant i Skibby for at bestille din aftensmad. Vi står klar til at tilberede den til dig!
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-4">
            <a
              href="tel:+4529832959"
              className="w-full sm:w-auto flex items-center justify-center gap-3 bg-brand-primary hover:bg-brand-primary/90 text-white font-semibold px-8 py-4 rounded-full transition-all shadow-lg"
            >
              Ring Take-away (29 83 29 59)
            </a>
            <Link
              to="/kontakt"
              className="w-full sm:w-auto flex items-center justify-center gap-3 bg-transparent hover:bg-transparent border border-white text-white font-semibold px-8 py-4 rounded-full transition-all"
            >
              Se adresse & info
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
